import React, { useEffect, useMemo, useState } from "react";
import { Button, Modal, Tooltip, message } from "antd";
import { DownloadOutlined, FilePdfOutlined, PrinterOutlined, QrcodeOutlined } from "@ant-design/icons";
import { eventUseMutation } from "../../hooks";
import {
    downloadCheckQrPdf,
    downloadCheckQrPng,
    getCheckQrCoordinatesKey,
    getCheckQrStorageKey,
    printCheckQr,
    readCheckQrRevision,
    removeCheckQrRevision,
    withCheckQrRevision,
    writeCheckQrRevision,
} from "../../helpers/event-check-qr-helper";
import {
    hasMeaningfulCheckCoordinates,
    isValidCheckRadius,
} from "../../helpers/event-check-location-helper";
import Icons from "components/icons";
import { Localize } from "components/service";


const EventCheckQrGenerate = ({
    event,
    eventID,
    icon,
    label,
    modalTitle = false,
    variant = "default",
    qrStatus,
    qrUrl: controlledQrUrl,
    onGenerateQr,
    disabled = false,
    buttonClassName = "",
}) => {
    const initialQrUrl = event?.getCheckQrCodeUrl;
    const [ modalOpen, setModalOpen ] = useState(false);
    const [ qrUrl, setQrUrl ] = useState(() => withCheckQrRevision(
        initialQrUrl,
        readCheckQrRevision(eventID)
    ));
    const [ exportLoading, setExportLoading ] = useState(null);

    const staffManagementVariant = variant === "staff-management";
    const controlled = Boolean(qrStatus);
    const canGenerate = (
        hasMeaningfulCheckCoordinates(event?.check_latitude, event?.check_longitude) &&
        (!staffManagementVariant || isValidCheckRadius(event?.check_radius))
    );
    const modalTitleID = `event-check-qr-modal-title-${ eventID }`;
    const currentCoordinatesKey = useMemo(() => getCheckQrCoordinatesKey(event), [ event ]);
    const qrStorageKey = useMemo(() => getCheckQrStorageKey(eventID), [ eventID ]);
    const hasQrUrl = !!qrUrl;
    let storedCoordinatesKey = null;

    if (!controlled)
    {
        try
        {
            storedCoordinatesKey = window.localStorage.getItem(qrStorageKey);
        } catch (error)
        {
            storedCoordinatesKey = null;
        }
    }
    const needsQrRefresh = !controlled && hasQrUrl && storedCoordinatesKey && storedCoordinatesKey !== currentCoordinatesKey;
    const controlledLoading = [ "generating", "regenerating" ].includes(qrStatus);
    const controlledReady = qrStatus === "ready";
    const fillCoordinatesLabel = Localize({
        children: "CHECK_QR.Tooltip_FillCoordinates",
    }).props.children;
    const storeCurrentCoordinatesKey = () => {
        try
        {
            window.localStorage.setItem(qrStorageKey, currentCoordinatesKey);
        } catch (error)
        {
            // Opening or generating the QR must not depend on browser storage availability.
        }
    };

    useEffect(() => {
        setQrUrl(controlled ?
            controlledQrUrl :
            withCheckQrRevision(
                event?.getCheckQrCodeUrl,
                readCheckQrRevision(eventID)
            )
        );
    }, [ controlled, controlledQrUrl, event?.getCheckQrCodeUrl, eventID ]);

    useEffect(() => {
        if (controlled && !controlledReady)
        {
            setModalOpen(false);
        }
    }, [ controlled, controlledReady ]);

    const {
        _setEventCheckQrGenerate,
        loadingMutationEventCheckQrGenerate,
    } = eventUseMutation.checkQrGenerate({
        onCompleted: (payload) => {
            const nextQrUrl = payload?.exhibitionEvent?.getCheckQrCodeUrl;

            if (!nextQrUrl)
            {
                message.error(<Localize>CHECK_QR.Text_NoQrUrl</Localize>);
                return;
            }

            const previousQrUrl = event?.getCheckQrCodeUrl;
            const nextRevision = previousQrUrl && nextQrUrl === previousQrUrl ?
                String(Date.now()) :
                null;

            if (nextRevision)
            {
                writeCheckQrRevision(eventID, nextRevision);
            } else
            {
                removeCheckQrRevision(eventID);
            }

            setQrUrl(withCheckQrRevision(nextQrUrl, nextRevision));
            storeCurrentCoordinatesKey();
            setModalOpen(true);
        }
    });

    const openQr = async () => {
        if (controlled)
        {
            if (controlledReady && qrUrl)
            {
                setModalOpen(true);
                return;
            }

            if (controlledLoading || !onGenerateQr)
            {
                return;
            }

            const nextQrUrl = await onGenerateQr();

            if (nextQrUrl)
            {
                setQrUrl(nextQrUrl);
                setModalOpen(true);
            }

            return;
        }

        if (qrUrl && !needsQrRefresh)
        {
            if (!storedCoordinatesKey)
            {
                storeCurrentCoordinatesKey();
            }

            setModalOpen(true);
            return;
        }

        _setEventCheckQrGenerate({ variables: { ee_id: eventID } });
    };

    const controlledButtonLabel = [ "outdated", "regenerating" ].includes(qrStatus) ?
        <Localize>CHECK_QR.Button_Regenerate</Localize> :
        [ "generation_failed", "regeneration_failed" ].includes(qrStatus) ?
            <Localize>CHECK_QR.Button_Retry</Localize> :
            controlledReady ?
                <Localize>CHECK_QR.Button_View</Localize> :
                <Localize>CHECK_QR.Button_Create</Localize>;
    const defaultButtonLabel = controlled ?
        controlledButtonLabel :
        hasQrUrl && !needsQrRefresh ?
            <Localize>CHECK_QR.Button_View</Localize> :
            needsQrRefresh ?
                <Localize>CHECK_QR.Button_Update</Localize> :
                <Localize>CHECK_QR.Button_Generate</Localize>;

    const exportPoster = async (type, action) => {
        setExportLoading(type);

        try
        {
            const completed = await action();

            if (completed === false) throw new Error("QR poster export failed");
        } catch (error)
        {
            message.error(<Localize>CHECK_QR.Text_ExportError</Localize>);
        } finally
        {
            setExportLoading(null);
        }
    };

    const posterParams = {
        url: qrUrl,
        eventID,
        eventTitle: event?.title,
    };
    const downloadPng = () => exportPoster("png", () => downloadCheckQrPng(posterParams));
    const downloadPdf = () => exportPoster("pdf", () => downloadCheckQrPdf(posterParams));
    const printQr = () => exportPoster("print", () => printCheckQr(posterParams));

    const button = (
        <Button
            className={ `ant-btn-secondary event-check-qr-generate__button ${ buttonClassName }`.trim() }
            disabled={ disabled || !canGenerate || (controlled && !controlledReady && !onGenerateQr) }
            loading={ controlled ? controlledLoading : loadingMutationEventCheckQrGenerate }
            onClick={ openQr }>
            { icon || <QrcodeOutlined /> }
            <span>{ label || defaultButtonLabel }</span>
        </Button>
    );

    return (
        <>
            { canGenerate ?
                button :
                <Tooltip title={ fillCoordinatesLabel }>
                    <span
                        className="event-check-qr-generate__disabled"
                        tabIndex={ 0 }
                        aria-label={ fillCoordinatesLabel }>
                        { button }
                    </span>
                </Tooltip>
            }

            <Modal
                className={ `modal-standard event-check-qr-modal ${ staffManagementVariant ? "event-check-qr-modal--staff-management" : "" }` }
                centered
                visible={ modalOpen }
                footer={ null }
                modalRender={ (modal) => React.cloneElement(modal, {
                    ref: (node) => node?.closest("[role='dialog']")?.setAttribute("aria-labelledby", modalTitleID),
                }) }
                width={ staffManagementVariant ? 380 : 420 }
                destroyOnClose={ true }
                onCancel={ () => setModalOpen(false) }>
                <strong id={ modalTitleID } className="modal-title">
                    { modalTitle || <Localize>CHECK_QR.Modal_Title</Localize> }
                </strong>

                <div className="event-check-qr-modal__content">
                    { qrUrl &&
                        <img
                            className="event-check-qr-modal__image"
                            src={ qrUrl }
                            alt="QR Code" />
                    }

                    <div className="event-check-qr-modal__actions">
                        <Button
                            className={ `event-check-qr-modal__action-button event-check-qr-modal__action-button--primary${ staffManagementVariant ? " btn-icon-text" : "" }` }
                            disabled={ !!exportLoading }
                            loading={ exportLoading === "png" }
                            onClick={ downloadPng }>
                            { staffManagementVariant ? <Icons.Download /> : <DownloadOutlined /> }
                            <Localize>CHECK_QR.Button_DownloadPng</Localize>
                        </Button>
                        <Button
                            className={ `event-check-qr-modal__action-button${ staffManagementVariant ? " btn-icon-text event-check-qr-modal__action-button--primary" : "" }` }
                            disabled={ !!exportLoading }
                            onClick={ downloadPdf }
                            loading={ exportLoading === "pdf" }>
                            { staffManagementVariant ? <Icons.Download /> : <FilePdfOutlined /> }
                            <Localize>CHECK_QR.Button_DownloadPdf</Localize>
                        </Button>
                        <Button
                            className={ `event-check-qr-modal__action-button event-check-qr-modal__action-button--print${ staffManagementVariant ? " btn-icon-text" : "" }` }
                            disabled={ !!exportLoading }
                            loading={ exportLoading === "print" }
                            onClick={ printQr }>
                            { staffManagementVariant ? <Icons.StaffPrinter /> : <PrinterOutlined /> }
                            <Localize>CHECK_QR.Button_Print</Localize>
                        </Button>
                    </div>
                </div>
            </Modal>
        </>
    );
};

export default EventCheckQrGenerate;
